import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import ExamQuestionCard, { lettersOf, sameAnswer } from '../components/ExamQuestionCard';
import { useToast } from '../hooks/useToast';
import { saveRecord } from '../utils/storage';
import { fetchExamSets, fetchQuestionsBySet, ExamSetSummary, SetExamQuestion } from '../utils/questionApi';
import { getExamByKey, getSessionExamIds, allowedSetNumbers } from '../data/examCatalog';

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

type Step = 'intro' | 'select-set' | 'exam' | 'result';
type ExamMode = 'practice' | 'exam' | null;
type Filter = 'all' | 'correct' | 'incorrect' | 'unanswered';

interface ExamPageProps {
  /** Exam key used when the URL has no ?exam= (e.g. the legacy /sap-exam route). */
  defaultExam?: string;
}

const ExamPage: React.FC<ExamPageProps> = ({ defaultExam = 'sap' }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [examKey] = useState(() => searchParams.get('exam') || defaultExam);
  const exam = getExamByKey(examKey);
  const examId = exam?.id ?? 0;
  const EXAM_NAME = exam?.name ?? 'Exam';
  // keep ?exam= in the URL whenever the page updates its own query string
  const withExam = (extra: Record<string, string>) => ({ exam: examKey, ...extra });
  const { msg, type, visible, showToast } = useToast();

  const [step, setStep] = useState<Step>('intro');
  const [examMode, setExamMode] = useState<ExamMode>(null);
  const [selectedSet, setSelectedSet] = useState<number>(1);

  const [sets, setSets] = useState<ExamSetSummary[]>([]);
  const [loadingSets, setLoadingSets] = useState(true);
  const [setsError, setSetsError] = useState('');

  const [questions, setQuestions] = useState<SetExamQuestion[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const [answers, setAnswers] = useState<Record<number, number[]>>({});
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [expandedQuestions, setExpandedQuestions] = useState<Record<number, boolean>>({});
  const [scoreData, setScoreData] = useState<{ score: number; total: number } | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  // Auth check
  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('userLoggedIn') === '1' && sessionStorage.getItem('dashUser');
    if (!isLoggedIn) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Only exams assigned to this user (Admin > Users & Access) can be opened
  useEffect(() => {
    if (!exam || !getSessionExamIds().includes(exam.id)) {
      navigate('/dashboard', { replace: true });
    }
  }, [exam, navigate]);

  // Sets (and their question counts) come from Supabase
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!examId) return;
      const res = await fetchExamSets(examId);
      if (cancelled) return;
      if (res.success && res.sets) setSets(res.sets);
      else setSetsError(res.error || 'Could not load the question sets.');
      setLoadingSets(false);
    })();
    return () => { cancelled = true; };
  }, [examId]);

  // URL drives the flow: ?mode=practice|exam  →  choose a set;  &set=N  →  load that set's questions
  useEffect(() => {
    const mode = searchParams.get('mode');
    const setId = searchParams.get('set');
    if (mode !== 'practice' && mode !== 'exam') return;
    setExamMode(mode);

    if (!setId) {
      setStep('select-set');
      return;
    }

    const setNo = parseInt(setId, 10);
    if (isNaN(setNo)) {
      setStep('select-set');
      return;
    }

    let cancelled = false;
    (async () => {
      setSelectedSet(setNo);
      setLoadingQuestions(true);
      const res = await fetchQuestionsBySet(examId, setNo);
      if (cancelled) return;
      setLoadingQuestions(false);
      if (!res.success || !res.questions || res.questions.length === 0) {
        showToast(res.locked ? '🔒 This set is not included in your plan.' : res.success ? '⚠️ This set has no questions yet.' : '⚠️ Could not load the questions. Please try again.', 'error');
        setSearchParams(withExam({ mode }));
        setStep('select-set');
        return;
      }
      // Practice keeps the stored order; exam mode shuffles the questions
      setQuestions(mode === 'exam' ? shuffleArray(res.questions) : res.questions);
      setAnswers({});
      setChecked({});
      setExpandedQuestions({});
      setScoreData(null);
      setFilter('all');
      setStep('exam');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    })();
    return () => { cancelled = true; };
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalQ = questions.length;
  const answeredCount = Object.values(answers).filter(a => a.length > 0).length;
  const progressPct = totalQ ? Math.round((answeredCount / totalQ) * 100) : 0;
  const unlockedSets = allowedSetNumbers(sets.map(s => s.set));
  const totalAvailable = sets.reduce((n, s) => n + s.count, 0);

  const toggleAnswer = (qIdx: number, optIdx: number) => {
    if (checked[qIdx]) return;
    const required = questions[qIdx].ans.length;
    setAnswers(prev => {
      const cur = prev[qIdx] || [];
      if (required === 1) return { ...prev, [qIdx]: [optIdx] };
      if (cur.includes(optIdx)) return { ...prev, [qIdx]: cur.filter(x => x !== optIdx) };
      const next = [...cur, optIdx];
      return { ...prev, [qIdx]: next.length > required ? next.slice(next.length - required) : next };
    });
  };

  const checkAnswer = (qIdx: number) => setChecked(prev => ({ ...prev, [qIdx]: true }));

  const submitExam = useCallback(() => {
    const allChecked: Record<number, boolean> = {};
    questions.forEach((_, i) => { allChecked[i] = true; });
    setChecked(allChecked);

    let score = 0;
    questions.forEach((q, i) => { if (sameAnswer(answers[i] || [], q.ans)) score++; });

    saveRecord('exam_submissions', {
      category: examKey,
      categoryName: `${EXAM_NAME} · Set ${selectedSet}`,
      set: selectedSet,
      score,
      total: questions.length,
      percentage: Math.round(score / questions.length * 100),
      answers: { ...answers },
      source: `${examKey}_exam`,
    });

    setSearchParams({ exam: examKey });
    setScoreData({ score, total: questions.length });
    setStep('result');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [questions, answers, selectedSet, setSearchParams, examKey, EXAM_NAME]);

  const pill = (active: boolean, color: string): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: '20px', cursor: 'pointer',
    border: active ? `2px solid ${color}` : '1px solid var(--gray-300)',
    background: active ? color : '#fff', color: active ? '#fff' : 'var(--gray-700)',
  });

  return (
    <Layout>
      <Toast msg={msg} type={type} visible={visible} />

      <div className="page-hero">
        <span className="section-tag">{exam?.short ?? 'Exam'}</span>
        <h1>{EXAM_NAME}</h1>
        <p>Practice questions for the {exam?.short ?? ''} exam.</p>
      </div>

      <div className="section exam-section">
        <div className="container" style={{ maxWidth: '1100px' }}>

          {/* STEP 1: Introduction */}
          {step === 'intro' && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '4rem', marginBottom: '20px' }}>{exam?.emoji}</div>
              <h2 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '16px', color: 'var(--gray-800)' }}>{EXAM_NAME}</h2>
              <p style={{ fontSize: '1.1rem', color: 'var(--gray-600)', marginBottom: '32px' }}>
                {loadingSets ? 'Loading question sets…' : setsError ? setsError : `Test your knowledge with ${totalAvailable} practice questions across ${sets.length} sets.`}
              </p>

              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-blue btn-lg" onClick={() => setSearchParams(withExam({ mode: 'practice' }))} style={{ minWidth: '200px' }}>
                  📝 Practice Mode
                </button>
                <button className="btn btn-outline-blue btn-lg" onClick={() => setSearchParams(withExam({ mode: 'exam' }))} style={{ minWidth: '200px' }}>
                  🎯 Exam Mode
                </button>
              </div>

              <div style={{ marginTop: '40px', padding: '20px', background: 'var(--blue-pale)', borderRadius: 'var(--radius)', maxWidth: '500px', margin: '40px auto 0' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '12px', color: 'var(--blue)' }}>📚 About This Exam</h3>
                <ul style={{ textAlign: 'left', fontSize: '0.9rem', color: 'var(--gray-700)', paddingLeft: '20px', lineHeight: '1.8' }}>
                  <li>{sets.length} sets of questions</li>
                  <li>Single-answer and multiple-answer questions</li>
                  <li>Exam mode shuffles the questions each attempt</li>
                  <li>Detailed explanations and a full review at the end</li>
                </ul>
              </div>
            </div>
          )}

          {/* STEP 1b: Select Exam Set */}
          {step === 'select-set' && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '8px' }}>Select Exam Set</h2>
              <p style={{ marginBottom: '8px', color: 'var(--gray-600)' }}>
                {examMode === 'practice' ? '📝 Practice Mode' : examMode === 'exam' ? '🎯 Exam Mode' : ''} — Choose a set of questions
              </p>
              <p style={{ marginBottom: '32px', color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                {examMode === 'practice' ? 'Questions stay in order, see answers after each question' : 'Questions are shuffled, submit all at once'}
              </p>

              {(loadingSets || loadingQuestions) && <p style={{ color: 'var(--gray-500)' }}>{loadingQuestions ? 'Loading questions…' : 'Loading sets…'}</p>}
              {!loadingSets && setsError && <p style={{ color: 'var(--red)' }}>{setsError}</p>}
              {!loadingSets && !setsError && sets.length === 0 && <p style={{ color: 'var(--gray-500)' }}>No question sets are available yet.</p>}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '400px', margin: '0 auto' }}>
                {sets.map(s => {
                  const locked = !unlockedSets.includes(s.set);
                  return (
                    <button
                      key={s.set}
                      onClick={() => !locked && setSearchParams(withExam({ mode: examMode || 'practice', set: String(s.set) }))}
                      disabled={loadingQuestions || locked}
                      title={locked ? 'Not included in your plan' : undefined}
                      className={`btn ${selectedSet === s.set && loadingQuestions ? 'btn-blue' : 'btn-outline-blue'} btn-lg`}
                      style={{ justifyContent: 'space-between', padding: '16px 24px', ...(locked ? { opacity: 0.5, cursor: 'not-allowed', background: 'var(--gray-100)', borderColor: 'var(--gray-300)', color: 'var(--gray-500)' } : {}) }}
                    >
                      <span>{locked ? '🔒 ' : ''}Set {s.set}</span>
                      <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>{locked ? 'Locked' : `${s.count} questions`}</span>
                    </button>
                  );
                })}
              </div>

              <button className="btn btn-outline-gray" onClick={() => { setSearchParams(withExam({})); navigate('/dashboard'); }} style={{ marginTop: '24px' }}>
                ← Back to Dashboard
              </button>
            </div>
          )}

          {/* STEP 2: Questions */}
          {step === 'exam' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--gray-800)' }}>Set {selectedSet}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>{examMode === 'exam' ? '🎯 Exam Mode' : '📝 Practice Mode'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 600 }}>
                    {answeredCount} of {totalQ} answered
                  </div>
                  <div className="progress-bar-wrap" style={{ width: '180px', marginTop: '5px' }}>
                    <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                </div>
              </div>

              {questions.map((q, qIdx) => (
                <ExamQuestionCard
                  key={q.id || qIdx}
                  question={q}
                  qIdx={qIdx}
                  selected={answers[qIdx] || []}
                  isChecked={!!checked[qIdx]}
                  isPracticeMode={examMode === 'practice'}
                  onToggle={toggleAnswer}
                  onCheck={checkAnswer}
                />
              ))}

              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                {examMode === 'exam' && (
                  <button className="btn btn-blue btn-lg" onClick={submitExam}>
                    {answeredCount < totalQ ? `Submit (${answeredCount}/${totalQ} answered)` : 'Submit & See Results'}
                  </button>
                )}
                {examMode === 'practice' && (
                  <button className="btn btn-outline-blue btn-lg" onClick={() => { setSearchParams(withExam({ mode: 'practice' })); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                    ← Choose another set
                  </button>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Results */}
          {step === 'result' && scoreData && (
            <div>
              <div style={{ background: 'linear-gradient(135deg, var(--blue-pale), var(--orange-pale))', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-lg)', padding: '32px', marginBottom: '24px', textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>
                  {scoreData.score / scoreData.total >= 0.8 ? '🎉' : '📚'}
                </div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-800)', marginBottom: '8px' }}>
                  Set {selectedSet} · Your Score: {scoreData.score}/{scoreData.total}
                </h2>
                <p style={{ fontSize: '1.1rem', fontWeight: 700, color: scoreData.score / scoreData.total >= 0.8 ? 'var(--green)' : 'var(--orange)' }}>
                  {Math.round(scoreData.score / scoreData.total * 100)}% — {scoreData.score / scoreData.total >= 0.8 ? 'Excellent!' : 'Keep practicing!'}
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '18px' }}>
                  <button className="btn btn-blue" onClick={() => setSearchParams(withExam({ mode: 'exam', set: String(selectedSet) }))}>Retake Set {selectedSet}</button>
                  <button className="btn btn-outline-blue" onClick={() => setSearchParams(withExam({ mode: 'exam' }))}>Choose another set</button>
                  <button className="btn btn-outline-gray" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
                </div>
              </div>

              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, marginBottom: '16px', marginTop: '32px' }}>📝 Review All Questions</h3>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <button onClick={() => setFilter('all')} style={pill(filter === 'all', 'var(--blue)')}>All</button>
                <button onClick={() => setFilter('correct')} style={pill(filter === 'correct', 'var(--green)')}>Correct</button>
                <button onClick={() => setFilter('incorrect')} style={pill(filter === 'incorrect', 'var(--red)')}>Incorrect</button>
                <button onClick={() => setFilter('unanswered')} style={pill(filter === 'unanswered', 'var(--orange)')}>Not Answered</button>
                <button
                  onClick={() => {
                    const allExpanded = questions.every((_, i) => expandedQuestions[i]);
                    setExpandedQuestions(allExpanded ? {} : Object.fromEntries(questions.map((_, i) => [i, true])));
                  }}
                  style={{ padding: '8px 16px', borderRadius: '20px', border: '1px solid var(--gray-300)', background: '#fff', color: 'var(--gray-700)', cursor: 'pointer', marginLeft: 'auto' }}
                >
                  {questions.every((_, i) => expandedQuestions[i]) ? 'Collapse All' : 'Expand All'}
                </button>
              </div>

              {questions.map((q, qIdx) => {
                const userAns = answers[qIdx] || [];
                const isUnanswered = userAns.length === 0;
                const isCorrect = !isUnanswered && sameAnswer(userAns, q.ans);
                const isExpanded = !!expandedQuestions[qIdx];

                if (filter === 'correct' && !isCorrect) return null;
                if (filter === 'incorrect' && (isCorrect || isUnanswered)) return null;
                if (filter === 'unanswered' && !isUnanswered) return null;

                const fmt = (idxs: number[]) => idxs.map(i => `${String.fromCharCode(65 + i)}. ${q.opts[i]}`).join('  |  ');

                return (
                  <div key={q.id || qIdx} style={{ background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius)', padding: '20px', marginBottom: '12px' }}>
                    <div onClick={() => setExpandedQuestions(prev => ({ ...prev, [qIdx]: !prev[qIdx] }))} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 700, fontSize: '1.05rem', color: isCorrect ? 'var(--green)' : isUnanswered ? 'var(--orange)' : 'var(--red)' }}>
                        {isCorrect ? '✅ Correct' : isUnanswered ? '⚠️ Not Answered' : '❌ Incorrect'} - Question {qIdx + 1}
                      </span>
                      <span style={{ fontSize: '1.2rem' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>

                    {isExpanded && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ fontSize: '0.9rem', marginBottom: '12px', fontWeight: 600, whiteSpace: 'pre-wrap' }}>{q.q}</div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginBottom: '8px' }}>
                          Your Answer: {isUnanswered ? 'Not answered' : fmt(userAns)}
                        </div>
                        {!isCorrect && (
                          <div style={{ fontSize: '0.85rem', color: 'var(--green)', marginBottom: '8px' }}>
                            Correct Answer{q.ans.length > 1 ? 's' : ''} ({lettersOf(q.ans)}): {fmt(q.ans)}
                          </div>
                        )}
                        {q.explain && (
                          <div style={{ marginTop: '12px', padding: '14px', background: 'var(--blue-pale)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--blue-soft)' }}>
                            <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '8px', color: 'var(--blue)' }}>📚 Explanation</div>
                            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.92rem', color: 'var(--gray-700)', margin: 0 }}>
                              {q.explain}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ExamPage;
